import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { KaisatsuResponseError } from '../../../shared/api/response.error';
import { SchoolAdminService } from '../../../shared/api/school-admin.service';
import {
  CurrentFilterSet,
  PageParams,
  SearchSchoolYear,
  SsoSearchOptions,
  SsoUsersList,
  SsoUsersSearchParams,
  UsersSearchParams,
} from '../../../shared/api/types';
import { GlobalMessageService } from '../../../shared/global-message.service';
import { kaisatsuHandleError } from '../../../shared/kaisatsu-error-handler';
import { SsoTeachersService } from '../../../shared/sso-teachers.service';
import { changeUsersSearchParams } from '../change-users-search-params';
import { NgIf } from '@angular/common';
import { SsoUsersSearchFieldComponent } from '../search-field/sso-users-search-field.component';
import { SsoUsersListComponent } from '../users-list/sso-users-list.component';

@Component({
  selector: 'app-sso-teachers',
  templateUrl: './sso-teachers.component.html',
  styleUrls: ['./sso-teachers.component.scss'],
  imports: [NgIf, SsoUsersSearchFieldComponent, SsoUsersListComponent],
})
export class SsoTeachersComponent implements OnInit, OnDestroy {
  @Input()
  searchOptions!: SsoSearchOptions;

  @Output()
  schoolYearChange = new EventEmitter<number>();

  currentFilter: CurrentFilterSet = {
    school_year: null,
    stage_id: null,
    grade_id: null,
    classroom_id: null,
    attendance_number: null,
    email: null,
  };

  teachersList: SsoUsersList = { count: 0 };
  searchInProgress = false;

  private onDestroy$ = new Subject<void>();

  constructor(
    private readonly schoolAdminService: SchoolAdminService,
    private readonly ssoTeachersService: SsoTeachersService,
    private readonly globalMessageService: GlobalMessageService
  ) {}

  ngOnInit(): void {
    this.ssoTeachersService
      .getCurrentFilterAndTeachers()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(async (state) => {
        // Filterと検索結果の情報が残っていればそれを表示
        if (state?.currentFilter) {
          this.currentFilter = state.currentFilter;
          this.teachersList = state.usersList;
        } else {
          // ない場合は初期状態(取得したsearchOptionsのschool_yearがselectedの年度)の生徒一覧を取得
          if (this.selectedSchoolYear) {
            const selectedSchoolYear = this.selectedSchoolYear.year;
            this.currentFilter.school_year = selectedSchoolYear;
            await this.searchTeachers({
              school_year: selectedSchoolYear,
              ...this.currentPageParams,
            });
          }
        }
      });
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
  }

  currentFilterUpdate(value: Partial<CurrentFilterSet>): void {
    if (typeof value.school_year === 'number') {
      this.schoolYearChange.emit(value.school_year);
    }
    this.currentFilter = {
      ...this.currentFilter,
      ...value,
    };
    // 現在の検索条件と先生一覧を保存
    this.ssoTeachersService.setCurrentFilterAndTeachers(
      this.currentFilter,
      this.teachersList
    );
  }

  async searchTeachers(params: SsoUsersSearchParams): Promise<void> {
    this.searchInProgress = true;
    try {
      this.teachersList =
        await this.schoolAdminService.getSsoTeachersList(params);
      // 現在の検索条件と先生一覧を保存
      this.ssoTeachersService.setCurrentFilterAndTeachers(
        this.currentFilter,
        this.teachersList
      );
    } catch (e: KaisatsuResponseError | unknown) {
      if (e instanceof KaisatsuResponseError) {
        // グローバルに表示するエラーメッセージをここで保存
        this.globalMessageService.setErrorMessage(e.messages);
      } else {
        kaisatsuHandleError(e, this.globalMessageService);
      }
    }
    this.searchInProgress = false;
  }

  private get selectedSchoolYear(): SearchSchoolYear | undefined {
    return this.searchOptions.school_years.find(
      (school_year) => school_year.selected
    );
  }

  async getPageTeachers(pageParams: PageParams): Promise<void> {
    const params = {
      ...this.currentUsersSearchParams,
      ...pageParams,
    };

    await this.searchTeachers(params);
  }

  async refreshTeachers(): Promise<void> {
    await this.searchTeachers({
      ...this.currentUsersSearchParams,
      ...this.currentPageParams,
    });
  }

  private get currentUsersSearchParams(): UsersSearchParams {
    return changeUsersSearchParams(this.currentFilter);
  }

  private get currentPageParams(): PageParams {
    return { per_page: 10, page: this.teachersList.rels?.current || 1 };
  }
}
